import React from 'react';

// @ts-ignore
import userAgent from 'lib/currentUserAgent';
// @ts-ignore
import AuthActions from 'auth/AuthActions';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CoachingHeading from 'coaching/components/CoachingHeading';
import { consume } from 'lib/queryParams';
import Container from 'components/Container';
import Columns2 from 'components/Columns2';
import Display from 'components/Display';
import EventTracking from 'lib/EventTracking';
import Grid, { GapSize } from 'components/Grid';
import GridItem from 'components/GridItem';
import Heading from 'components/Heading';
import Image from 'components/Image';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import SwirlyDown from 'assets/images/coaching/swirly_down.svg';
import Focus from 'assets/images/coaching/focus.svg';
import Build from 'assets/images/coaching/build.svg';
import Customize from 'assets/images/coaching/customize.svg';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import { browserHistory } from 'react-router';

type Props = {
  onContinue: () => void;
  hasOptOutExperiment: boolean;
};

type State = {
  isSignedIn: boolean;
  loading: boolean;
};

class CoachingSelfServeIntroView extends React.Component<Props, State> {
  state: State = {
    isSignedIn: false,
    loading: true,
  };

  async componentDidMount() {
    const user = await AuthActions.checkLoggedInAndLoadAppData().catch(() =>
      this.setState({ loading: false })
    );

    this.setState({ isSignedIn: !!user, loading: false }, () => {
      if (!this.state.isSignedIn) {
        // if the user is not signed in, we should try and parse the
        // user ID from the URL if we have it, in order to maintain
        // consistency in our data pipeline across the entire user flow
        const params = consume(browserHistory, ['user_id']);
        const userId = params.user_id;
        EventTracking.setUserInfo(userId);
      }
    });

    this.trackEvent('view');
  }

  eventPrefix = 'coaching.self_schedule_purchase.plans_summary';

  trackEvent(key: string) {
    EventTracking.trackKey(`${this.eventPrefix}.${key}`);
  }

  renderPlans() {
    return [
      <Row size="small" key="weekly">
        <Row size="xxsmall">
          <MediaObject gutter="medium" center>
            <Heading level="4" flush>
              Weekly plan
            </Heading>
            <TextWithIcon icon="heart" inline iconSize="medium" iconColor="pencil">
              <Text size="xxxsmall">Most popular</Text>
            </TextWithIcon>
          </MediaObject>
        </Row>
        <Text size="small">Build on progress with one 40-minute session per week</Text>
      </Row>,
      <Row size="small" key="custom">
        <Row size="xxsmall">
          <Heading level="4" flush>
            Custom or on-demand
          </Heading>
        </Row>
        <Text size="small">Extend sessions to 60 minutes or schedule them as needed</Text>
      </Row>,
    ];
  }

  renderContent() {
    const { hasOptOutExperiment } = this.props;
    const ctaText = this.state.isSignedIn ? 'Continue' : 'Continue to log in';
    const gapSize: GapSize = 'small';
    const benefitGridProps = userAgent.mobile()
      ? { rows: 3, rowGap: gapSize, columns: 1, columnGap: gapSize }
      : { columns: 3, columnGap: gapSize };

    return (
      <Container size="medium">
        {hasOptOutExperiment ? (
          <>
            <CoachingHeading size="small">Get started</CoachingHeading>
            <Row>
              <Box color="eraser" textAlign="left" noShadow>
                <BoxBody size="xsmall">
                  <Row size="xxsmall">
                    <Text size="small" weight="semibold">
                      The Remind Coaching plan
                    </Text>
                  </Row>
                  <Row size="xxsmall">
                    <Text size="small">One 40-minute session per week</Text>
                  </Row>
                </BoxBody>
                <BoxBody light size="xsmall" rounded>
                  <Row size="xxsmall">
                    <Text size="small" color="graphite">
                      $40 per session billed weekly
                    </Text>
                  </Row>
                  <Row size="xxsmall">
                    <Text size="small" color="graphite">
                      Your first session is free, and you can cancel or adjust your plan any time.
                    </Text>
                  </Row>
                </BoxBody>
              </Box>
            </Row>
            <Row size="xxxlarge">
              <Button
                eventKey={`${this.eventPrefix}.continue.click`}
                onClick={this.props.onContinue}
                color="koolaid"
                level="primary"
                loading={this.state.loading}>
                {ctaText}
              </Button>
            </Row>
            <Row>
              <Grid {...benefitGridProps}>
                <BenefitHighlight
                  imageSrc={Focus}
                  headline="Progress and growth"
                  detail="Personalized instruction for current challenges and long-term successs"
                />
                <BenefitHighlight
                  imageSrc={Build}
                  headline="Build a weekly routine"
                  detail="We'll reserve the same day and time each week for you to work with your coach"
                />
                <BenefitHighlight
                  imageSrc={Customize}
                  headline="Flexible scheduling"
                  detail="Reschedule, skip, or add sessions to your plan as needed"
                />
              </Grid>
            </Row>
          </>
        ) : (
          <>
            <CoachingHeading size="medium">Try Remind Coaching</CoachingHeading>
            <Row size="xxsmall">
              <Heading level="4">
                Sign up and schedule a <Text weight="bold">free</Text> full-length coaching session
              </Heading>
            </Row>
            <Row size="small">
              <Text color="graphite" size="small">
                No subscriptions or strings attached.
              </Text>
            </Row>

            <Row size="xxsmall">
              <Heading level="4">If you like your free session, you can sign up for more.</Heading>
            </Row>
            <Row size="xxxlarge">
              <Text color="graphite" size="small">
                After you try it out, it's{' '}
                <Text weight="semibold">$40 for a 40-minute session.</Text>
              </Text>
            </Row>
          </>
        )}

        {!hasOptOutExperiment && (
          <>
            <Row size="xxxlarge">
              <Button
                eventKey={`${this.eventPrefix}.continue.click`}
                onClick={this.props.onContinue}
                color="koolaid"
                level="primary"
                loading={this.state.loading}>
                {ctaText}
              </Button>
            </Row>
            <Box color="paper" textAlign="left" noShadow noBorder>
              <BoxBody size="xsmall">
                <Row>
                  <Text size="small">
                    You'll be able to continue with the option that works for you—and make changes
                    any time.
                  </Text>
                </Row>
                <Display hide="480">
                  <Columns2 gutter="xxxxlarge" divider>
                    {this.renderPlans()}
                  </Columns2>
                </Display>
                <Display show="480">{this.renderPlans()}</Display>
              </BoxBody>
            </Box>
            {/* We place the image relative to the bottom of the screen so that resizing will still place it in the same area */}
            <Display hide="1024">
              <Image
                src={SwirlyDown}
                style={{
                  position: 'relative',
                  bottom: '427px',
                  right: '45px',
                  width: '40px',
                  height: '100px',
                }}
              />
            </Display>
          </>
        )}
      </Container>
    );
  }

  render() {
    return <Container size="medium">{this.renderContent()}</Container>;
  }
}

type BenefitHighlightProps = {
  imageSrc: string;
  headline: string;
  detail: string;
};

function BenefitHighlight({ imageSrc, headline, detail }: BenefitHighlightProps) {
  return (
    <GridItem>
      <Row size="xsmall">
        <Image src={imageSrc} />
      </Row>
      <Row size="xxsmall">
        <Text size="small" weight="semibold">
          {headline}
        </Text>
      </Row>
      <Row size="xxsmall">
        <Text size="xsmall" color="graphite">
          {detail}
        </Text>
      </Row>
    </GridItem>
  );
}

export default CoachingSelfServeIntroView;
